import { FindingsTableItem } from "../../models/tables";
import { RepositoryProjectBranch } from "../utils";

interface FindingResponse {
  status: string;
  message?: string;
  finding?: FindingsTableItem;
}

export class FindingRepository extends RepositoryProjectBranch {
  apiUrl = '/api/v1/findings/';

  private compareVersions(v1: string | null, v2: string | null): number {
    if (v1 === null) return -1;
    if (v2 === null) return 1;

    try {
      const v1Parts = v1.split('.').map(part => {
        // Extract numeric portion for comparison, handling pre-release suffixes
        const match = part.match(/^\d+/);
        return match ? parseInt(match[0], 10) : 0;
      });
      const v2Parts = v2.split('.').map(part => {
        const match = part.match(/^\d+/);
        return match ? parseInt(match[0], 10) : 0;
      });

      // Compare each part of the version
      for (let i = 0; i < Math.max(v1Parts.length, v2Parts.length); i++) {
        const v1Part = v1Parts[i] || 0;
        const v2Part = v2Parts[i] || 0;
        if (v1Part > v2Part) return 1;
        if (v1Part < v2Part) return -1;
      }
      return 0;
    } catch (error) {
      console.error(`Error comparing versions ${v1} and ${v2}:`, error);
      // If there's an error in comparison, treat the existing highest version as higher
      return -1;
    }
  }

  async getByScan(scanId: number): Promise<FindingsTableItem[]> {
    const response = await fetch(this.apiUrl + "?scan_id=" + scanId);
    return response.status === 200 ? await response.json().then(data => data) as FindingsTableItem[] : [];
  }

  async getMetaData(): Promise<object> {
    const response = await fetch(this.apiUrl, { method: "OPTIONS" });
    return response.status === 200 ? await response.json().then(data => data) as object : {};
  }

  async updateStatus(findingUUID: string, newStatus: string, csrfToken: string): Promise<FindingResponse> {
    const response = await fetch(
      this.apiUrl + findingUUID + "/",
      {
        method: "PATCH",
        body: JSON.stringify({ status: newStatus }),
        headers: {
          "X-CSRFToken": csrfToken,
          "Content-Type": "application/json"
        }
      }
    );
    if (response.ok && response.headers.get("Content-Length") !== "0") {
        return response.json();
    }
    return { status: "success" };
  }

  async ignore(findingUUID: string, reason: string, expires: string, csrfToken: string): Promise<FindingResponse> {
    const response = await fetch(
      this.apiUrl + findingUUID + "/ignore/",
      {
        method: "POST",
        body: JSON.stringify({ reason: reason, expires: expires }),
        headers: {
          "X-CSRFToken": csrfToken,
          "Content-Type": "application/json"
        }
      }
    );
    if (response.ok && response.headers.get("Content-Length") !== "0") {
        return response.json();
    }
    return { status: "success" };
  }

  private createPackageLocationKey(packageId: string, location: string): string {
    // Create a safe identifier that can be used as an object key and in the UI
    return `${packageId}:${encodeURIComponent(location)}`;
  }

  groupByPackage(data: FindingsTableItem[]) {
    if (!data || !Array.isArray(data)) {
      return [];
    }

    const groups = data.reduce((acc, finding) => {
      if (!finding?.details?.package_id) {
        console.warn('Invalid finding data:', finding);
        return acc;
      }
      const packageId = finding.details.package_id;
      const location = finding.details.location || '';
      const groupKey = this.createPackageLocationKey(packageId, location);

      if (!acc[groupKey]) {
        // Initialize group with the exact structure we need
        acc[groupKey] = {
          package_id: packageId,
          location: location,
          findings: [],
          highest_epss: 0,
          highest_cvss: 0,
          latest_seen: 0,
          highest_secure_version: null,
          stages: new Set<string>(),
          group_id: groupKey,
          total_risk_score: 0
        };
      }

      // Add the finding to the group
      acc[groupKey].findings.push({
        ...finding,
        risk_score: finding.risk_score || { score: 0, label: 'none' },
        cvss_score: finding.cvss_score || null,
        exploitability_score: finding.exploitability_score || null,
        archived_at: finding.archived_at || null,
        branches: finding.branches || [],
        stages: finding.stages || [],
        remediation: finding.remediation || '',
        highest_secure_version: finding.highest_secure_version || ''
      });

      // Update group level aggregations
      acc[groupKey].highest_epss = Math.max(acc[groupKey].highest_epss, finding.exploitability_score || 0);
      acc[groupKey].highest_cvss = Math.max(acc[groupKey].highest_cvss, finding.cvss_score || 0);
      acc[groupKey].latest_seen = Math.max(acc[groupKey].latest_seen, finding.date_last_seen || 0);

      // Update highest secure version
      if (finding.highest_secure_version &&
          (acc[groupKey].highest_secure_version === null ||
           this.compareVersions(finding.highest_secure_version, acc[groupKey].highest_secure_version) > 0)) {
        acc[groupKey].highest_secure_version = finding.highest_secure_version;
      }

      // Add stages
      if (Array.isArray(finding.stages)) {
        finding.stages.forEach(stage => acc[groupKey].stages.add(stage));
      }

      // Update total risk score
      acc[groupKey].total_risk_score = Math.max(
        acc[groupKey].total_risk_score,
        finding.risk_score?.score || 0
      );

      return acc;
    }, {} as Record<string, any>);

    // Convert to array and ensure consistent structure
    return Object.values(groups).map(group => ({
      ...group,
      stages: Array.from(group.stages),
      findings: group.findings.map((finding: any) => ({
        ...finding,
        branches: finding.branches || [],
        stages: finding.stages || [],
        risk_score: finding.risk_score || { score: 0, label: 'none' },
        project: finding.project || {},
        details: {
          ...finding.details,
          vulnerability_id: finding.details.vulnerability_id || ''
        },
        type_shorthand: finding.type_shorthand || '📦 Package Vulnerability'
      }))
    }));
  }
}
